import { Image as Img } from '@cp/ui';
import React, { ReactNode, useMemo, useState } from 'react';
import { ClientPhotoPurpose } from '@common/model/client/photo/purpose';
import { ClientPhotoSize } from '@common/const/client/photo';
import { ClientPhotoViewModel } from '@common/model/client/photo/view';
import { ImageAssetItem } from '@common/model/resource/image';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { getResizedAssetItem, getResizedAssetItems } from '@cp/shared/image';
import { PlayButton } from '@cp/ds/src/components/playButton';
import { useIsMobile } from '@cp/ds/src/hooks';
import { Stack } from '@cp/ds/src/components/stack';
import { styled } from '@cp/ds/src/theme/styled';
import { AudioPlayer } from '@cp/ds/src/components/audioPlayer';
import { ResumeCardStatsNew } from 'src/entities/resume/ui/card/statsNew';
import { UserMainData } from 'src/entities/resume/ui/card/userMainData';
import { useCatalogItemMedia } from 'src/entities/resume/model';
import VectorVertical from 'src/assets/shapes/vectors/vectorVertical.svg';

export const AUDIO_PLAYER_MODAL_NAME = 'audioPlayer';

type ItemResumeCardProps = {
  achievements?: ReactNode | ReactNode[];
  onlineStatus?: ReactNode;
  data: CatalogItemViewModel;
  favoriteButton?: React.ReactNode;
};

const Root = styled(Stack)(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: 'white',
  width: '100%',
  maxWidth: '480px',
  [theme.breakpoints.up('md')]: {
    borderRadius: '16px',
    maxWidth: '100%',
  },
}));

const PhotoWrapper = styled(Stack)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  zIndex: 0,
  [theme.breakpoints.up('md')]: {
    maxWidth: '334px',
  },
}));

const AchievementsWrapper = styled(Stack)({
  justifyContent: 'flex-start',
  gap: '4px',
  position: 'absolute',
  left: '16px',
  top: '16px',
  zIndex: 2,
  padding: '2px',
});

const PlayButtonWrapper = styled(Stack)({
  position: 'absolute',
  left: '50%',
  bottom: '20px',
  transform: 'translateX(-50%)',
  zIndex: 2,
});

const StatsWrapper = styled(Stack)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.down('md')]: {
    padding: '8px 8px 12px',
    borderTop: `1px solid ${theme.palette['gray middle']}`,
    borderBottom: `1px solid ${theme.palette['gray middle']}`,
  },
}));

const VectorWrapper = styled(Stack)(({ theme }) => ({
  color: theme.palette['gray background'],
  position: 'absolute',
  top: 0,
  right: 0,
  transform: 'scale(-1)',
  width: '65px',
}));

const mainPhotoToAsset = (photo: ClientPhotoViewModel, size: number): ImageAssetItem => {
  const resizedAsset = getResizedAssetItem(photo.image.base, size);
  return resizedAsset || { src: photo.notCroppedOriginalSrc as string, width: size, height: size };
};

export const ItemResumeCard = ({ achievements, data, onlineStatus, favoriteButton }: ItemResumeCardProps) => {
  const isMobile = useIsMobile();
  const [play, setPlay] = useState(false);
  const { media: catalogItemMedia, icon: CatalogItemMediaIcon } = useCatalogItemMedia(data);

  const { mainAsset, assets } = useMemo(() => {
    const clientPhoto =
      data?.clientProfile?.photoSet?.find((p) => p.purpose === ClientPhotoPurpose.Resume) ?? data?.clientProfile?.mainPhoto;

    if (!clientPhoto) {
      return { assets: [] as ReturnType<typeof getResizedAssetItems> };
    }
    return {
      assets: getResizedAssetItems(clientPhoto.image, ClientPhotoSize.Medium),
      mainAsset: mainPhotoToAsset(clientPhoto, ClientPhotoSize.Medium),
    };
  }, [data?.clientProfile?.mainPhoto, data?.clientProfile?.photoSet]);

  const handleMediaButtonClick = () => {
    setPlay(!play);
  };

  return (
    <Root direction={{ xs: 'column', md: 'row' }} justifyContent="flex-start">
      <PhotoWrapper>
        <picture>
          {assets.map((asset) => (
            <source key={asset.src} srcSet={asset.src} type={asset.mimeType} />
          ))}
          <Img
            alt={[data.clientProfile?.name, data.clientProfile?.surname].filter(Boolean).join(' ')}
            height={mainAsset?.height}
            loading={'lazy'}
            src={mainAsset?.src}
            sx={{ maxWidth: '100%', height: 'auto', display: 'block' }}
            width={mainAsset?.width}
          />
        </picture>
        <AchievementsWrapper alignItems="flex-start">{achievements}</AchievementsWrapper>
        {favoriteButton}
        <PlayButtonWrapper>
          {catalogItemMedia && CatalogItemMediaIcon ? (
            play ? (
              <AudioPlayer
                duration={catalogItemMedia.durationInMs}
                onClick={handleMediaButtonClick}
                size="small"
                source={catalogItemMedia.src}
              />
            ) : (
              <PlayButton onClick={handleMediaButtonClick} size={3}>
                <CatalogItemMediaIcon />
              </PlayButton>
            )
          ) : null}
        </PlayButtonWrapper>
      </PhotoWrapper>
      <StatsWrapper direction={{ xs: 'row', md: 'column' }} justifyContent="space-between">
        {!isMobile && <UserMainData data={data} onlineStatus={onlineStatus} />}
        <ResumeCardStatsNew data={data} fontSize={isMobile ? 'medium' : 'large'} />
      </StatsWrapper>
      {!isMobile && (
        <VectorWrapper>
          <VectorVertical />
        </VectorWrapper>
      )}
    </Root>
  );
};
